import React, { useState } from "react";
import { motion } from "framer-motion";
import Select from "../ui/select";
import Button from "../ui/button";
import { Calculator, Download, Share2 } from "lucide-react";

const roomTypes = {
  living: { name: "Living Room", basePrice: 80000, multiplier: 1.2 },
  bedroom: { name: "Bedroom", basePrice: 60000, multiplier: 1.0 },
  kitchen: { name: "Kitchen", basePrice: 100000, multiplier: 1.5 },
  bathroom: { name: "Bathroom", basePrice: 50000, multiplier: 0.8 },
  office: { name: "Home Office", basePrice: 70000, multiplier: 1.1 },
};

const qualityLevels = {
  basic: {
    name: "Basic",
    multiplier: 1.0,
    features: ["Standard materials", "Essential fixtures", "Basic finishes"],
  },
  premium: {
    name: "Premium",
    multiplier: 1.5,
    features: ["Quality materials", "Designer fixtures", "Premium finishes"],
  },
  luxury: {
    name: "Luxury",
    multiplier: 2.2,
    features: [
      "High-end materials",
      "Premium fixtures",
      "Luxury finishes",
      "Custom elements",
    ],
  },
};

export default function BudgetCalculator() {
  const [roomType, setRoomType] = useState("living");
  const [area, setArea] = useState(200);
  const [quality, setQuality] = useState("premium");
  const [includeElectrical, setIncludeElectrical] = useState(true);
  const [includeFurniture, setIncludeFurniture] = useState(true);

  const calculateBudget = () => {
    const room = roomTypes[roomType];
    const qualityLevel = qualityLevels[quality];

    let base =
      (area / 100) *
      room.basePrice *
      room.multiplier *
      qualityLevel.multiplier;

    if (includeElectrical) base += (area / 100) * 15000;
    if (includeFurniture) base += (area / 100) * 25000;

    return {
      min: Math.round(base * 0.85),
      max: Math.round(base * 1.15),
      average: Math.round(base),
    };
  };

  const budget = calculateBudget();
  const pricePerSqFt = Math.round(budget.average / area);

  return (
    <section className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-16">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-12 h-[1px] bg-[#C9A962]" />
            <span className="text-[#C9A962] tracking-[0.3em] text-xs uppercase">
              Budget Planner
            </span>
            <div className="w-12 h-[1px] bg-[#C9A962]" />
          </div>
          <h2 className="font-serif text-4xl md:text-5xl text-[#2C2C2C] mb-4">
            Estimate Your Project Cost
          </h2>
          <p className="text-[#2C2C2C]/60 max-w-2xl mx-auto text-lg">
            Get an instant estimate for your interior design project.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Inputs */}
          <div className="bg-[#FDFBF7] p-8">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 bg-[#C9A962] flex items-center justify-center">
                <Calculator className="w-5 h-5 text-white" />
              </div>
              <h3 className="font-serif text-2xl">Project Details</h3>
            </div>

            <div className="space-y-8">
              {/* Room Type */}
              <Select
                value={roomType}
                onChange={setRoomType}
                options={Object.entries(roomTypes).map(([key, r]) => ({
                  label: r.name,
                  value: key,
                }))}
              />

              {/* Area */}
              <div>
                <label className="block text-sm mb-2">
                  Area: {area} sq ft
                </label>
                <input
                  type="range"
                  min={50}
                  max={1000}
                  step={10}
                  value={area}
                  onChange={(e) => setArea(Number(e.target.value))}
                  className="w-full accent-[#C9A962]"
                />
              </div>

              {/* Quality */}
              <Select
                value={quality}
                onChange={setQuality}
                options={Object.entries(qualityLevels).map(([key, q]) => ({
                  label: q.name,
                  value: key,
                }))}
              />

              {/* Extras */}
              <div className="space-y-3">
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={includeElectrical}
                    onChange={(e) => setIncludeElectrical(e.target.checked)}
                    className="accent-[#C9A962]"
                  />
                  Electrical & Lighting
                </label>
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={includeFurniture}
                    onChange={(e) => setIncludeFurniture(e.target.checked)}
                    className="accent-[#C9A962]"
                  />
                  Furniture & Decor
                </label>
              </div>
            </div>
          </div>

          {/* Results */}
          <div className="bg-[#2C2C2C] p-8 text-white">
            <h3 className="font-serif text-2xl mb-6">Estimated Budget</h3>

            <div className="text-4xl text-[#C9A962] mb-2">
              ₹{(budget.min / 100000).toFixed(1)}L – ₹
              {(budget.max / 100000).toFixed(1)}L
            </div>

            <div className="text-white/60 mb-6">
              Avg: ₹{(budget.average / 100000).toFixed(2)}L
            </div>
            <div className="border-t border-white/10 pt-6 mb-6">
                <div className="flex justify-between items-center mb-4">
                  <span className="text-white/60">Per Sq Ft Cost</span>
                  <span className="font-serif text-xl text-[#C9A962]">₹{pricePerSqFt}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-white/60">Project Timeline</span>
                  <span className="font-serif text-xl">4-8 Weeks</span>
                </div>
              </div>

              <div className="bg-white/5 p-4 rounded mb-6">
                <div className="text-xs text-white/60 uppercase tracking-wider mb-3">Includes</div>
                <ul className="space-y-2 text-sm">
                  {qualityLevels[quality].features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <div className="w-1 h-1 bg-[#C9A962] rounded-full" />
                      {feature}
                    </li>
                  ))}
                  {includeElectrical && (
                    <li className="flex items-center gap-2">
                      <div className="w-1 h-1 bg-[#C9A962] rounded-full" />
                      Electrical & Lighting
                    </li>
                  )}
                  {includeFurniture && (
                    <li className="flex items-center gap-2">
                      <div className="w-1 h-1 bg-[#C9A962] rounded-full" />
                      Furniture & Decor
                    </li>
                  )}
                </ul>
              </div>

            <div className="flex gap-3">
              <Button className="flex-1 bg-[#C9A962] text-white">
                <Download className="w-4 h-4 mr-2" />
                Download
              </Button>
              <Button className="flex-1 border border-white/20 text-white">
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
